import type {
  UserPreference,
  WorkspacePreference,
  PreferencesMap,
  SetPreferenceInput,
} from '@maker-toolchest/shared';
import { api } from '@/lib/api';

export const preferencesApi = {
  // ========================================================================
  // USER PREFERENCES
  // ========================================================================

  /**
   * Get all user preferences, optionally filtered by prefix
   */
  getUserPreferences: async (prefix?: string): Promise<PreferencesMap> => {
    const searchParams = new URLSearchParams();
    if (prefix) searchParams.set('prefix', prefix);

    const query = searchParams.toString();
    const response = await api.get<PreferencesMap>(`/preferences/user${query ? `?${query}` : ''}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch user preferences');
    }
    return response.data;
  },

  /**
   * Get a single user preference by key
   */
  getUserPreference: async (key: string): Promise<any | null> => {
    const response = await api.get<{ key: string; value: any }>(`/preferences/user/${key}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch user preference');
    }
    return response.data.value;
  },

  /**
   * Set a single user preference
   */
  setUserPreference: async (key: string, value: any): Promise<UserPreference> => {
    const response = await api.post<UserPreference>('/preferences/user', { key, value });
    if (!response.success || !response.data) {
      throw new Error('Failed to set user preference');
    }
    return response.data;
  },

  /**
   * Set multiple user preferences at once
   */
  setUserPreferences: async (preferences: SetPreferenceInput[]): Promise<UserPreference[]> => {
    const response = await api.post<UserPreference[]>('/preferences/user', preferences);
    if (!response.success || !response.data) {
      throw new Error('Failed to set user preferences');
    }
    return response.data;
  },

  /**
   * Delete a user preference
   */
  deleteUserPreference: async (key: string): Promise<void> => {
    await api.delete(`/preferences/user/${key}`);
  },

  // ========================================================================
  // WORKSPACE PREFERENCES
  // ========================================================================

  /**
   * Get all workspace preferences, optionally filtered by prefix
   */
  getWorkspacePreferences: async (prefix?: string): Promise<PreferencesMap> => {
    const searchParams = new URLSearchParams();
    if (prefix) searchParams.set('prefix', prefix);

    const query = searchParams.toString();
    const response = await api.get<PreferencesMap>(`/preferences/workspace${query ? `?${query}` : ''}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch workspace preferences');
    }
    return response.data;
  },

  /**
   * Get a single workspace preference by key
   */
  getWorkspacePreference: async (key: string): Promise<any | null> => {
    const response = await api.get<{ key: string; value: any }>(`/preferences/workspace/${key}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch workspace preference');
    }
    return response.data.value;
  },

  /**
   * Set a single workspace preference (requires OWNER/ADMIN role)
   */
  setWorkspacePreference: async (key: string, value: any): Promise<WorkspacePreference> => {
    const response = await api.post<WorkspacePreference>('/preferences/workspace', { key, value });
    if (!response.success || !response.data) {
      throw new Error('Failed to set workspace preference');
    }
    return response.data;
  },

  /**
   * Set multiple workspace preferences at once (requires OWNER/ADMIN role)
   */
  setWorkspacePreferences: async (preferences: SetPreferenceInput[]): Promise<WorkspacePreference[]> => {
    const response = await api.post<WorkspacePreference[]>('/preferences/workspace', preferences);
    if (!response.success || !response.data) {
      throw new Error('Failed to set workspace preferences');
    }
    return response.data;
  },

  /**
   * Delete a workspace preference (requires OWNER/ADMIN role)
   */
  deleteWorkspacePreference: async (key: string): Promise<void> => {
    await api.delete(`/preferences/workspace/${key}`);
  },
};
